import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthUser } from "../../contexts/AuthUserContext";
import { fetchDailyOverviewDates } from "../../utils/api/reportApi";
import styles from "./DailyOverviewViewer.module.css";

const HIDDEN_SECTION_LABELS = new Set([
  "seo and visibility",
  "content and social",
  "market context",
]);

const DailyDatePicker = ({ availableDates, selectedReportId, onSelect, loading }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const selected = availableDates.find((d) => d.reportId === selectedReportId);
  const label = selected?.dateLabel || (loading ? "Loading…" : "Select date");

  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <div className={styles.ddpWrap} ref={ref}>
      <button
        type="button"
        className={styles.ddpTrigger}
        onClick={() => setOpen((v) => !v)}
        disabled={loading}
      >
        <span className={styles.ddpTriggerLabel}>{label}</span>
        <span className={styles.ddpTriggerChevron}>▼</span>
      </button>

      {open && (
        <div className={styles.ddpDropdown}>
          {availableDates.length === 0 ? (
            <span className={styles.ddpEmpty}>No reports found</span>
          ) : (
            availableDates.map((d) => (
              <button
                key={d.reportId}
                type="button"
                className={`${styles.ddpItem} ${
                  d.reportId === selectedReportId ? styles.ddpItemActive : ""
                }`}
                onClick={() => {
                  onSelect(d);
                  setOpen(false);
                }}
              >
                {d.dateLabel || d.dateKey || d.reportId}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

const MetricItem = ({ label, value, change }) => {
  const changeText = String(change || "").trim();
  const changeClass = changeText.startsWith("+")
    ? styles.metricChangePositive
    : changeText.startsWith("-")
      ? styles.metricChangeNegative
      : /healthy|strong|good/i.test(changeText)
        ? styles.metricChangePositive
        : /weak|risk|below|low/i.test(changeText)
          ? styles.metricChangeNegative
          : styles.metricChangeWarning;

  return (
    <div className={styles.metricItem}>
      <span className={styles.metricLabel}>{label}</span>
      <span className={styles.metricValue}>{value}</span>
      {changeText ? (
        <span className={`${styles.metricChange} ${changeClass}`}>{changeText}</span>
      ) : null}
    </div>
  );
};

const StatusPill = ({ status }) => {
  if (!status?.label) return null;
  const toneClass =
    status.tone === "positive"
      ? styles.statusPositive
      : status.tone === "warning"
        ? styles.statusWarning
        : status.tone === "negative"
          ? styles.statusNegative
          : styles.statusNeutral;
  return <span className={`${styles.statusPill} ${toneClass}`}>{status.label}</span>;
};

const OverviewCard = ({ card, onOpen }) => {
  const hasMetrics = Array.isArray(card.metrics) && card.metrics.length > 0;
  const canOpen = !!card.sourceReportId;
  const handleOpen = () => {
    if (!canOpen) return;
    onOpen(card.sourceReportId, card);
  };

  return (
    <div
      className={`${styles.card} ${canOpen ? styles.cardInteractive : ""}`}
      onClick={canOpen ? handleOpen : undefined}
      role={canOpen ? "button" : undefined}
      tabIndex={canOpen ? 0 : undefined}
      onKeyDown={
        canOpen
          ? (event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                handleOpen();
              }
            }
          : undefined
      }
    >
      <div className={styles.cardTop}>
        <div className={styles.cardTitleRow}>
          <span className={styles.cardTitle}>{card.title}</span>
        </div>
        {canOpen ? (
          <button
            type="button"
            className={styles.cardChevron}
            onClick={(event) => {
              event.stopPropagation();
              handleOpen();
            }}
            aria-label={`Open ${card.title}`}
          >
            ›
          </button>
        ) : null}
      </div>

      {hasMetrics ? (
        <div className={styles.metricsGrid}>
          {card.metrics.map((m) => (
            <MetricItem
              key={`${card.id}-${m.label}`}
              label={m.label}
              value={m.value}
              change={m.change}
            />
          ))}
        </div>
      ) : null}

      {card.insight || card.note ? (
        <p className={styles.cardInsight}>{card.insight || card.note}</p>
      ) : null}

      <div className={styles.cardFooter}>
        <StatusPill status={card.status} />
      </div>

      {!hasMetrics && !card.insight && !card.note ? (
        <p className={styles.cardEmpty}>No data available yet.</p>
      ) : null}
    </div>
  );
};

const SectionBlock = ({ section, onOpen }) => {
  if (!section?.cards?.length) return null;
  return (
    <section className={styles.sectionBlock}>
      <div className={styles.sectionLabel}>{section.label}</div>
      <div className={section.cards.length === 1 ? styles.singleCardGrid : styles.cardGrid}>
        {section.cards.map((entry) => (
          <OverviewCard key={entry.id} card={entry} onOpen={onOpen} />
        ))}
      </div>
    </section>
  );
};

const ActionCenter = ({ items = [] }) => {
  if (!items.length) return null;
  return (
    <section className={styles.sectionBlock}>
      <div className={styles.sectionLabel}>Action center</div>
      <div className={styles.actionList}>
        {items.map((item, index) => (
          <div key={item.id || `${item.title}-${index}`} className={styles.actionItem}>
            <div className={styles.actionIndex}>{index + 1}</div>
            <div className={styles.actionBody}>
              <div className={styles.actionTitle}>{item.title}</div>
              {item.reason ? <div className={styles.actionReason}>{item.reason}</div> : null}
              {item.channel ? (
                <div className={styles.actionChannel}>Channel: {item.channel}</div>
              ) : null}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

const DailyOverviewViewer = ({ data, report }) => {
  const navigate = useNavigate();
  const { user } = useAuthUser();
  const authToken = user?.token || null;

  const [availableDates, setAvailableDates] = useState([]);
  const [datesLoading, setDatesLoading] = useState(false);
  const [comparisonReportId, setComparisonReportId] = useState(null);

  const currentReportId = report?._id?.toString() || report?.id || null;
  const userId = report?.user_id || null;
  const shopId = report?.shop_id || null;

  const sections = Array.isArray(data?.sections) && data.sections.length
    ? data.sections
    : [{ id: "legacy", label: "Overview", cards: Array.isArray(data?.cards) ? data.cards : [] }];
  const visibleSections = sections.filter((entry) => {
    const label = String(entry?.label || "").trim().toLowerCase();
    return !HIDDEN_SECTION_LABELS.has(label);
  });
  const actionCenter = Array.isArray(data?.action_center) ? data.action_center : [];
  const assessmentSummaries = Array.isArray(data?.assessment_summaries)
    ? data.assessment_summaries
    : [];
  const assessmentSummaryCards = assessmentSummaries.map((item, index) => ({
    id: item?.id || `assessment-${index}`,
    title: item?.label || "Untitled",
    sourceReportId: item?.sourceReportId || null,
    metrics: [],
    insight: item?.summary || "No summary available yet.",
    note: item?.summary || "No summary available yet.",
    status: item?.tone
      ? {
          label:
            item.tone === "positive"
              ? "Healthy"
              : item.tone === "warning"
                ? "Needs attention"
                : item.tone === "negative"
                  ? "At risk"
                  : "Neutral",
          tone: item.tone,
        }
      : null,
  }));
  const hasRenderableSections = visibleSections.some(
    (entry) => Array.isArray(entry?.cards) && entry.cards.length > 0,
  );
  const hasActionCenter = actionCenter.length > 0;
  const hasAssessmentSummaries = assessmentSummaries.length > 0;
  const shouldRenderAssessmentSnapshot = !hasRenderableSections && hasAssessmentSummaries;

  const fetchDates = useCallback(async () => {
    if (!userId) return;
    setDatesLoading(true);
    try {
      const result = await fetchDailyOverviewDates({ userId, shopId, authToken });
      if (result?.ok && Array.isArray(result.dates)) {
        setAvailableDates(result.dates);
        const fallbackComparison = result.dates.find(
          (entry) => entry?.reportId && entry.reportId !== currentReportId,
        );
        if (
          fallbackComparison?.reportId &&
          (!comparisonReportId || comparisonReportId === currentReportId)
        ) {
          setComparisonReportId(fallbackComparison.reportId);
        }
      }
    } catch {
      // silent degrade
    } finally {
      setDatesLoading(false);
    }
  }, [userId, shopId, authToken, currentReportId, comparisonReportId]);

  useEffect(() => {
    fetchDates();
  }, [fetchDates]);

  useEffect(() => {
    if (!availableDates.length) return;
    const comparisonMatchesPrimary = comparisonReportId === currentReportId;
    const comparisonMissing = comparisonReportId
      ? !availableDates.some((entry) => entry.reportId === comparisonReportId)
      : true;

    if (!comparisonMatchesPrimary && !comparisonMissing) return;

    const fallbackComparison = availableDates.find(
      (entry) => entry?.reportId && entry.reportId !== currentReportId,
    );
    setComparisonReportId(fallbackComparison?.reportId || null);
  }, [availableDates, comparisonReportId, currentReportId]);

  if (!data) return null;

  const handlePrimarySelect = (entry) => {
    if (entry.reportId && entry.reportId !== currentReportId) {
      navigate(`/reports/${entry.reportId}`);
    }
  };

  const handleComparisonSelect = (entry) => {
    if (!entry?.reportId || entry.reportId === currentReportId) return;
    setComparisonReportId(entry.reportId);
  };

  const openReport = (reportId, card = null) => {
    if (!reportId) return;
    const isSalesOrdersCard =
      card?.sourceKey === "salesOrdersRolling" ||
      card?.targetView === "salesOrders" ||
      card?.id === "sales-orders";
    const quickWindow = isSalesOrdersCard ? "today" : null;
    const search = quickWindow ? `?quickWindow=${encodeURIComponent(quickWindow)}` : "";
    navigate(`/reports/${reportId}${search}`, {
      state: {
        fromDailyOverview: true,
        dailyOverviewReturnTo: currentReportId ? `/reports/${currentReportId}` : "/reports",
        dailyOverviewLabel: "Daily Overview",
        salesOrdersQuickWindow: quickWindow,
      },
    });
  };

  const comparisonDates = availableDates.filter((d) => d.reportId !== currentReportId);
  const timeframeLabel = data?.timeframe?.label || "Today";
  const comparisonLabel = data?.timeframe?.comparisonLabel || "Previous";
  const timeframeWhy = data?.timeframe?.why || null;

  return (
    <div className={styles.wrapper}>

      <div className={styles.windowBar}>
        <div className={styles.windowBarLeft}>
          <DailyDatePicker
            availableDates={availableDates}
            selectedReportId={currentReportId}
            onSelect={handlePrimarySelect}
            loading={datesLoading}
          />
          {/* <span className={styles.windowVs}>vs</span>
          <DailyDatePicker
            availableDates={comparisonDates}
            selectedReportId={comparisonReportId}
            onSelect={handleComparisonSelect}
            loading={datesLoading}
          /> */}
        </div>
      </div>


      <header className={styles.hero}>
        {data?.dateLabel ? <p className={styles.dateLabel}>{data.dateLabel}</p> : null}
        {data?.headline ? <h2 className={styles.headline}>{data.headline}</h2> : null}
        {data?.subheadline ? <p className={styles.subheadline}>{data.subheadline}</p> : null}
      </header>

      {shouldRenderAssessmentSnapshot ? (
        <section className={styles.sectionBlock}>
          <div className={styles.sectionLabel}>Assessment snapshot</div>
          <div className={styles.cardGrid}>
            {assessmentSummaryCards.map((entry) => (
              <OverviewCard key={entry.id} card={entry} onOpen={openReport} />
            ))}
          </div>
        </section>
      ) : null}

      {visibleSections.map((entry) => (
        <SectionBlock key={entry.id} section={entry} onOpen={openReport} />
      ))}

      {/*
      <ActionCenter items={actionCenter} />
      */}

      {!hasRenderableSections && !hasActionCenter && !hasAssessmentSummaries ? (
        <section className={styles.emptyState}>
          <p className={styles.emptyStateTitle}>No daily overview details yet.</p>
          <p className={styles.emptyStateBody}>
            Zeqi created the report shell, but the upstream sections, cards, and action center have
            not been populated yet.
          </p>
        </section>
      ) : null}
    </div>
  );
};

export default DailyOverviewViewer;
